
import { useState } from 'react';

interface StatusCardProps {
  title: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  value: string | number;
  subtitle: string;
  lastUpdated?: string;
  onRun?: () => void;
  isRunning?: boolean;
  result?: CrawlerResult | null;
}

export interface CrawlerResult {
  success: boolean;
  message?: string;
  error?: string;
  details?: string;
  count?: number;
  fetched?: number;
  saved?: number;
  skipped?: number;
  updated_at?: string;
}

export default function StatusCard({
  title,
  icon,
  iconBg,
  iconColor,
  value,
  subtitle,
  lastUpdated,
  onRun,
  isRunning,
  result,
}: StatusCardProps) {
  const [showResult, setShowResult] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${iconBg}`}>
            <i className={`${icon} text-xl ${iconColor}`}></i>
          </div>
          {onRun && (
            <button
              onClick={onRun}
              disabled={isRunning}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                isRunning
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-red-50 text-red-600 hover:bg-red-100'
              }`}
            >
              {isRunning ? (
                <>
                  <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin"></div>
                  실행 중...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-fill text-sm"></i>
                  </div>
                  실행
                </>
              )}
            </button>
          )}
        </div>

        <div className="mb-1">
          <p className="text-xs font-medium text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
            {value}
          </p>
        </div>
        <p className="text-xs text-gray-400">{subtitle}</p>

        {lastUpdated && (
          <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-1.5 text-xs text-gray-400">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-time-line text-xs"></i>
            </div>
            <span>마지막 업데이트: {lastUpdated}</span>
          </div>
        )}
      </div>

      {result && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setShowResult(!showResult)}
            className="w-full flex items-center justify-between px-5 py-2.5 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <div
                className={`w-2 h-2 rounded-full ${result.success ? 'bg-emerald-400' : 'bg-red-400'}`}
              ></div>
              {result.success ? '성공' : '실패'}
            </span>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-arrow-${showResult ? 'up' : 'down'}-s-line text-sm`}></i>
            </div>
          </button>
          {showResult && (
            <div className="px-5 pb-4">
              <pre className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
